/**
 * PatternContentEpics
 * -------------------
 * WHAT: Renders the "Epics" section of a Pattern — a table of iteration-plan
 *       entries (Epic[] each containing task[]). Used by ContentDataForm when
 *       the template's UIComponent for a section is `PatternContentEpics`.
 *
 * WHY: Epics need a structured editor (EpicEditor) and a CSV export, which is
 *      why they have their own component instead of going through the generic
 *      ContentDataSection.
 *
 * STATE SHAPE: maintains a deserialized copy of the epics (using
 *       JSONSerializer to turn plain objects back into Epic class instances
 *       so methods on Epic work).
 */
import { Box, ExpandableSection, SpaceBetween } from '@amzn/awsui-components-core';
import {
  ContentAccessUser,
  ContentHistory,
  ContentWorkflow,
  Epic,
  ContentNode,
  ContentType,
} from '@amzn/awscat-pattern-library-service-model';
import deepEqual from 'deep-equal';
import React from 'react';
import constants from '../../../common/constants';
import JSONSerializer from '../../../common/JSONSerializer';
import { ContentSchemaSection, ContentSectionKey } from '../../../model/ContentSchemaSection';
import EpicCsvEntry from '../../../model/EpicCsvEntry';
import CsvDownloadButton from '../../comment/CsvDownloadButton';
import { EpicCsvFormat } from '../../common/EpicEditor/EpicColumnDefinition';
import EpicEditor from '../../common/EpicEditor/EpicEditor';
import ContentSectionHeader from '../../content/ContentSectionHeader';
import TutorialLink from '../../content/TutorialLink';
import TechwriterMetadataEditor from './TechwriterMetadataEditor';
import TechwriterMetadata from '../../../model/TechwriterMetadata';
// CSV-download RUM monitoring: records analytics for the "CSV" button clicks.
import { CSVDownloadId, RUMEventTracking } from '../../../monitoring';

export interface PatternContentEpicsProps {
  epics: Epic[];
  readonly: boolean;
  // Note: content shape here is `any` — upstream callers may pass plain objects
  // that aren't Epic[] yet. JSONSerializer in the constructor handles hydration.
  onContentChanged?: (
    sectionId: string,
    key: ContentSectionKey,
    newContent: any,
    validationError?: any,
  ) => void;
  expanded?: boolean;
  contentId: string;
  workflowId: number;
  showSectionalComments?: boolean;
  workflow?: ContentWorkflow;
  workflowHistory?: ContentHistory[];
  coAuthors?: ContentAccessUser[];
  content: ContentNode; // Carries abstract/href for TechwriterMetadataEditor.
  showTechwriterMetadata: boolean;
  sectionId: string;
}

interface State {
  // Hydrated copy of props.epics (Epic class instances, not plain objects).
  updatedEpics: Epic[];
  sectionExpanded: boolean;
  techwriterMetadata: TechwriterMetadata;
}

class PatternContentEpics extends React.Component<PatternContentEpicsProps, State> {
  private static defaultProps = {
    epics: [],
    expanded: true,
    readonly: false,
  };

  // Used to deserialize plain JSON epics back into Epic class instances.
  private readonly serializer: JSONSerializer;
  // CSV schema: header names + accessor functions. Imported from a shared module.
  private csvFormat: Array<{ header: string; value: (e: EpicCsvEntry) => any }> = EpicCsvFormat;

  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    this.serializer = new JSONSerializer();
    const { content } = props;
    this.state = {
      // `expanded` prop is intentionally ignored on first render — see
      // componentDidUpdate. This is the same pattern as ContentDataSection.
      sectionExpanded: true,
      // Hydrate the incoming epics array so instance methods on Epic work.
      updatedEpics: this.serializer.deserializeArray<Epic>(props.epics, Epic),
      techwriterMetadata: { abstract: content.abstract, href: content.href },
    };
  }

  /**
   * Sync local state on prop changes.
   *
   * PERF: `deepEqual(epics, prevEpics)` walks the whole array on every update.
   * For large patterns with many epics/tasks this can be noticeable — consider
   * switching to reference equality and letting the parent immutably update.
   *
   * BUG: techwriterMetadata is NOT re-synced from props here (unlike
   * ContentDataSection which syncs content.abstract/href). If the parent
   * updates abstract/href on this section externally, this component won't
   * reflect it.
   */
  public componentDidUpdate(prevProps: PatternContentEpicsProps) {
    const { epics: prevEpics } = prevProps;
    const { epics } = this.props;
    if (!deepEqual(epics, prevEpics)) {
      this.setState({
        updatedEpics: this.serializer.deserializeArray<Epic>(this.props.epics, Epic),
      });
    }
    if (prevProps.expanded !== this.props.expanded) {
      const { expanded } = this.props;
      this.setState({
        // @ts-expect-error TS2322 - strict migration
        sectionExpanded: expanded,
      });
    }
  }

  public render() {
    const {
      readonly,
      epics,
      contentId,
      showSectionalComments,
      workflow,
      workflowId,
      coAuthors,
      workflowHistory,
      showTechwriterMetadata,
      content,
    } = this.props;
    const { updatedEpics, sectionExpanded, techwriterMetadata } = this.state;
    // CSV export button — only shown when there's data worth exporting.
    // Wrapped in RUMEventTracking so the click is recorded in analytics.
    const downloadButton = epics.length ? (
      <Box float="right">
        <RUMEventTracking id={CSVDownloadId.Pattern}>
          <CsvDownloadButton
            data={this.generateEpicCsvEntries(epics)}
            format={this.csvFormat}
            filename={'epics.csv'}
            buttonText={'CSV'}
          />
        </RUMEventTracking>
      </Box>
    ) : null;
    // DEAD CODE: isGuideOrStrategy is computed but... actually IS used as
    // `disabled={!isGuideOrStrategy}` below. Correct.
    const isGuideOrStrategy = [ContentType.Guide, ContentType.Strategy].includes(
      // @ts-expect-error TS2345 - strict migration
      workflow?.draftContentMetadata?.contentType,
    );
    const techWriterMetadataEditor = (
      <SpaceBetween size="xxxs">
        <TechwriterMetadataEditor
          readonly={readonly}
          visible={showTechwriterMetadata}
          metadata={techwriterMetadata}
          onInputUpdated={this.updateTechwriterMetadata}
          nodeType={content.nodeType}
          disabled={!isGuideOrStrategy}
        />
      </SpaceBetween>
    );
    // Header differs between modes: readonly shows header + download button
    // side-by-side; edit mode shows header + tutorial link (CSV isn't useful
    // while editing).
    const headerComponent = readonly ? (
      <Box>
        <ContentSectionHeader
          sectionId={'epics'}
          title={constants.EPICS_LABEL}
          isEditable={false}
          isSortable={false}
          readonly={readonly}
        />
        {downloadButton}
      </Box>
    ) : (
      <ContentSectionHeader
        sectionId={'epics'}
        title={constants.EPICS_LABEL}
        isEditable={false}
        isSortable={false}
        readonly={readonly}
        headerInfo={
          <TutorialLink
            // @ts-expect-error TS2322 - strict migration
            contentType={workflow?.draftContentMetadata?.contentType}
            schemaSection={ContentSchemaSection.ContentSections}
            sectionId={'epics'}
          />
        }
      />
    );
    const epicEditorClassName = readonly ? 'pattern-content-box' : '';
    // EpicEditor is the actual table UI. `isEditable={!readonly}` switches
    // between display-only and inline-edit modes.
    const epicEditor = (
      <div className={epicEditorClassName}>
        <EpicEditor
          isEditable={!readonly}
          epics={updatedEpics || []}
          onEpicsChanged={this.epicChanged}
          contentId={contentId}
          workflowId={workflowId}
          showSectionalComments={showSectionalComments}
          workflow={workflow}
          workflowHistory={workflowHistory}
          coAuthors={coAuthors}
        />
      </div>
    );

    // Readonly doesn't use ExpandableSection — epics read linearly like a table.
    // Edit mode wraps in ExpandableSection so long epic lists can be collapsed.
    const displaySection = readonly ? (
      <SpaceBetween size="xxs">
        {headerComponent}
        {techWriterMetadataEditor}
        {epicEditor}
      </SpaceBetween>
    ) : (
      <div>
        <ExpandableSection
          header={headerComponent}
          variant="container"
          onChange={this.expandSection}
          expanded={sectionExpanded}
        >
          {techWriterMetadataEditor}
          {epicEditor}
        </ExpandableSection>
      </div>
    );
    return <div>{displaySection}</div>;
  }

  // Toggle expansion state — same pattern as ContentDataSection.
  // @ts-expect-error TS7006 - strict migration
  private expandSection = (e) => {
    e.preventDefault();
    this.setState({
      sectionExpanded: e.detail.expanded,
    });
  };

  /**
   * Bubble up epic changes to parent. The sectionId is hard-coded as 'epics'
   * because there's only ever one epics section per pattern.
   *
   * NOTE: also re-caches the new list in local state so the UI doesn't flicker
   * while waiting for the parent to echo the change back through props.
   */
  private epicChanged = (epics: Epic[]) => {
    const { onContentChanged } = this.props;
    if (onContentChanged) {
      onContentChanged('epics', ContentSectionKey.ContentData, epics);
    }

    this.setState({
      updatedEpics: epics,
    });
  };

  /**
   * Flatten Epic[] (tree of epic -> tasks) into a flat EpicCsvEntry[] for CSV export.
   * An epic with no tasks still produces one row (header-only); epics with
   * tasks produce one row per task.
   */
  private generateEpicCsvEntries = (epics: Epic[]) => {
    // @ts-expect-error TS7034 - strict migration
    const entries = [];
    epics.forEach((epic, epicIndex) => {
      const tasks = epic.tasks;
      if (tasks.length) {
        tasks.forEach((task, taskIndex) => {
          entries.push(new EpicCsvEntry(epic, epicIndex + 1, task, taskIndex + 1));
        });
      } else {
        entries.push(new EpicCsvEntry(epic, epicIndex + 1));
      }
    });
    // @ts-expect-error TS7005 - strict migration
    return entries;
  };

  /**
   * Abstract/href updates — identical to ContentDataSection.updateTechwriterMetadata.
   * DRY smell: this method is copy-pasted across ContentDataSection,
   * PatternContentEpics, and PatternContentTroubleshooting. Good candidate to
   * extract into a shared hook/helper when refactoring.
   */
  private updateTechwriterMetadata = (
    sectionKey: ContentSectionKey,
    value: any,
    validationError?: string,
  ) => {
    const { techwriterMetadata } = this.state;
    this.setState({
      techwriterMetadata: {
        ...techwriterMetadata,
        [sectionKey]: value,
      },
    });
    const { sectionId, onContentChanged } = this.props;
    if (onContentChanged) {
      onContentChanged(sectionId, sectionKey, value, validationError);
    }
  };
}

export default PatternContentEpics;
